/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of, NEVER } from 'rxjs';
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators';

import { ProjectCatalogType } from '@touchplan/fancy-referencer';

import { FancyFirebaseState } from '../common/fancy-firebase/services/fancy-firebase-state.service';
import { RouteUtils } from './route-utils.service';
import { FancyFirebaseProjectsApiBuilder } from './fancy-firebase-api-builder/fancy-firebase-projects-api-builder.service';
import { ProjectLegacy } from '../app-data/models/Project';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { IMISCProjectPreferencesStateService } from './project-misc-preference-state.service';

interface IRouteDataState {
	projectData: ProjectLegacy;
}

const INITIAL_STATE: IRouteDataState = {
	projectData: null,
};

/**
 * This service handle the state of data tied to route changes such as current project data.
 */
@Injectable({
	providedIn: 'root',
})
export class RouteDataStateService extends ComponentStore<IRouteDataState> {
	public projectData$: Observable<ProjectLegacy> = this.select((state) => state.projectData);
	private logger: ILoggerService = LoggerServiceFactory.create('RouteDataStateService');

	constructor(
		private routeUtils: RouteUtils,
		private fancyFirebaseState: FancyFirebaseState,
		private fancyFirebaseProjectsApiBuilder: FancyFirebaseProjectsApiBuilder,
		private miscProjectPreferencesStateService: IMISCProjectPreferencesStateService
	) {
		super(INITIAL_STATE);
		this.listenProjectDataRouteChanges();
	}

	private updateProjectDataState = this.updater((state, projectData: ProjectLegacy) => ({
		...state,
		projectData,
	}));

	private listenProjectDataRouteChanges() {
		this.updateProjectDataState(this.activatedRouteProjectData$());
	}

	private activatedRouteProjectData$(): Observable<ProjectLegacy> {
		return this.routeUtils.allRouteParam$.pipe(
			withLatestFrom(this.projectData$),
			switchMap(([route, currentProjectData]) => {
				if (route && route.paramMap.has('projectId')) {
					const projectId = route.paramMap.get('projectId');
					// If current project id is the same that URL contains then no emition is needed
					if (currentProjectData?.projectId === projectId) {
						return NEVER;
					}
					this.miscProjectPreferencesStateService.projectId = projectId;
					return this.fancyFirebaseState.getProjectCatalog$(projectId, ProjectCatalogType.ProjectData).pipe(
						switchMap(() => this.fancyFirebaseProjectsApiBuilder.getProjectObject(projectId).object$),
						catchError((err) => {
							this.logger.error(err);
							return of(null);
						})
					);
				} else {
					return of(null);
				}
			})
		);
	}
}
